// =============================================================================
// VandieZ shipper-web -- Ratings (/ratings)
// =============================================================================
// Star ratings + tags + reviews the shipper has received from drivers
// across all completed trips. Read-only.
//
// Backed by GET /users/me/ratings (shared with mobile-shipper and the
// driver app). Server returns a histogram + items list; we render an
// average headline + the 1-5 bar breakdown + the review feed.
// =============================================================================

import { AlertCircle, Star } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ratingsApi } from "@/api/client";
import { PageHeader } from "@/components/AppShell";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { RatingsResponse } from "@/types/ratings";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
}

function StarRow({ count }: { count: number }): React.JSX.Element {
  return (
    <div className="flex" aria-label={`${count} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={cn(
            "h-4 w-4",
            i <= count
              ? "fill-accent-brand text-accent-brand"
              : "fill-none text-muted-foreground/40",
          )}
        />
      ))}
    </div>
  );
}

export function RatingsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [data, setData] = useState<RatingsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (accessToken === null) {return;}
    const c = new AbortController();
    setLoading(true);
    ratingsApi
      .me(accessToken, c.signal)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((e: unknown) => {
        if (c.signal.aborted) {return;}
        setError(
          e instanceof ApiError ? e.problem.detail : "Couldn't load ratings.",
        );
      })
      .finally(() => { setLoading(false); });
    return () => { c.abort(); };
  }, [accessToken]);

  // The histogram comes back keyed by string ("1" → count) because
  // JSON doesn't preserve numeric keys. Coerce to number for rendering.
  const maxBar = (() => {
    if (data === null) {return 0;}
    return Math.max(1, ...Object.values(data.summary.histogram));
  })();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ratings"
        description="What drivers said about you after each trip. Shown publicly to drivers when they decide to bid."
      />

      {error !== null && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Loading ratings…
          </CardContent>
        </Card>
      ) : data === null ? null : data.summary.count === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" /> No ratings yet
            </CardTitle>
            <CardDescription>
              Once you complete a trip, the driver can rate you. Aim for
              clear pickup contact info and prompt cash handover — the
              two biggest drivers of repeat-bidder behaviour.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 fill-accent-brand text-accent-brand" />
                {data.summary.average.toFixed(2)} average
              </CardTitle>
              <CardDescription>
                Across {data.summary.count} trip
                {data.summary.count === 1 ? "" : "s"}.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const count = data.summary.histogram[String(star)] ?? 0;
                const width = (count / maxBar) * 100;
                return (
                  <div
                    key={star}
                    className="flex items-center gap-3 text-xs text-muted-foreground"
                  >
                    <span className="w-6 tabular-nums">{star} ★</span>
                    <div className="relative h-2 flex-1 rounded-full bg-muted">
                      <div
                        className="absolute inset-y-0 left-0 rounded-full bg-accent-brand"
                        style={{ width: `${width}%` }}
                      />
                    </div>
                    <span className="w-8 text-right tabular-nums">{count}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Reviews</CardTitle>
              <CardDescription>
                {data.items.length === 1
                  ? "1 review."
                  : `${data.items.length} reviews.`}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {data.items.map((r) => (
                <div
                  key={r.id}
                  className="space-y-1 border-b pb-4 last:border-b-0 last:pb-0"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <StarRow count={r.rating} />
                      <span className="text-sm font-medium">{r.rater_name}</span>
                      <span className="text-xs text-muted-foreground capitalize">
                        ({r.rater_role})
                      </span>
                    </div>
                    <Link
                      to={`/trips/${r.trip_id}`}
                      className="text-xs text-muted-foreground hover:underline"
                    >
                      Trip · {formatDate(r.created_at)}
                    </Link>
                  </div>
                  {r.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {r.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-muted px-2 py-0.5 text-xs"
                        >
                          {t.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                  )}
                  {r.review_text !== null && r.review_text.length > 0 && (
                    <p className="text-sm text-foreground">{r.review_text}</p>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
